import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import { ApiError, api } from "../api";
import { t } from "../i18n";
import type { Locale } from "../types";

/**
 * Otwarcie podpisanego dokumentu (PDF) kandydata.
 *
 * Zwykły `<a href>` na adres `/api/o/.../pdf` był nawigacją najwyższego poziomu,
 * która nie niesie tokenu z pamięci — serwer odpowiadał 401 „Potwierdź numer
 * telefonu", więc człowiek nie mógł otworzyć tego, co przed chwilą podpisał.
 * Tutaj pobieramy plik żądaniem z nagłówkiem autoryzacji i otwieramy go z
 * lokalnego blobu, w języku kandydata.
 */
export function DocumentLink({
  recordId,
  docKey,
  locale,
  className,
  style,
  children,
}: {
  recordId: string;
  docKey: string;
  locale: Locale;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setFailed(null);
    // Kartę otwieramy SYNCHRONICZNIE, jeszcze w geście kliknięcia. iOS Safari
    // (i webviews w aplikacjach) blokują `window.open`/`<a target=_blank>`
    // wywołane PO awaicie jako popup — a pobranie PDF-u to await. Otwieramy więc
    // pustą kartę od razu i dopiero podstawiamy jej adres po pobraniu. `opener`
    // zerujemy, żeby strona bloba nie sięgała z powrotem do naszego okna.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const blob = await api.documentPdf(recordId, docKey, locale);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) {
        tab.location.href = url;
      } else {
        // Popup zablokowany (webviews, twardo ustawiona blokada) — nawigujemy
        // BIEŻĄCĄ kartą, co działa wszędzie bez wyskakującego okna. Kandydat
        // wraca „wstecz", a stan onboardingu wczytuje się z tokenu w localStorage.
        // `<a download>` na blobie iOS ignoruje, a klik po awaicie i tak blokuje,
        // więc pobranie było tu ślepą uliczką — stąd nawigacja zamiast niego.
        window.location.href = url;
      }
      // Blob musi żyć na tyle długo, żeby nowa karta zdążyła go wczytać.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      if (tab && !tab.closed) tab.close();
      // Komunikat z serwera niesie treść (np. 410: „dokument zniknął z magazynu,
      // skontaktuj się z nami") — pokazujemy go zamiast ogólnika, gdy jest.
      setFailed(caught instanceof ApiError ? caught.message : t("documentError", locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={className}
        style={style}
        onClick={open}
        disabled={busy}
        aria-busy={busy || undefined}
      >
        {children ?? t("viewDocument", locale)}
      </button>
      {failed && (
        <span className="error" role="alert">
          {failed}
        </span>
      )}
    </>
  );
}
